import React, { useState } from "react";
import {
  Box,
  Button,
  CloseButton,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiClipboard, FiClock } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useSurveyStatus } from "../../dashboard/hooks/useSurveyStatus";

const DISMISSED_KEY = "survey_card_dismissed_codes";

function getDismissedCodes() {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "{}");
  } catch {
    return {};
  }
}

function dismissCode(code) {
  const map = getDismissedCodes();
  map[code] = Date.now() + 30 * 24 * 60 * 60 * 1000;
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
}

function isCodeDismissed(code) {
  const map = getDismissedCodes();
  const until = map[code];
  return until && Date.now() < until;
}

export function DashboardSurveyCard({ onStartSurvey }) {
  const { t } = useTranslation("common");
  const { status, loading } = useSurveyStatus();
  const [, setDismissVersion] = useState(0);

  if (loading) return null;

  const inProgress = status?.in_progress?.find(
    (s) => !isCodeDismissed(s.code)
  );

  const available = status?.available_surveys?.find(
    (s) => !isCodeDismissed(s.code)
  );

  const survey = inProgress || available;
  if (!survey) return null;

  const handleDismiss = () => {
    dismissCode(survey.code);
    setDismissVersion((v) => v + 1);
  };

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="md"
      borderLeft="3px solid"
      borderLeftColor="gwbi.500"
      p={4}
      position="relative"
    >
      <CloseButton
        size="sm"
        position="absolute"
        top={2}
        right={2}
        onClick={handleDismiss}
        aria-label={t("survey.dismiss", "Dismiss")}
      />
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={FiClipboard} color="gwbi.500" boxSize={5} />
          <Text fontWeight="bold" fontSize="md" color="gray.800">
            {survey.title || t("survey.available_title", "Survey available")}
          </Text>
        </HStack>
        {survey.estimated_minutes && (
          <HStack spacing={1}>
            <Icon as={FiClock} color="gray.400" boxSize={3} />
            <Text fontSize="xs" color="gray.500">
              {t("survey.estimated_time", "~{{min}} minutes", { min: survey.estimated_minutes })}
            </Text>
          </HStack>
        )}
        {inProgress && (
          <Text fontSize="xs" color="gwbi.600">
            {t("survey.in_progress", "{{pct}}% completed — pick up where you left off", {
              pct: survey.progress_pct,
            })}
          </Text>
        )}
        <Button
          colorScheme="gwbi"
          size="sm"
          onClick={() => onStartSurvey?.(survey.code)}
        >
          {inProgress
            ? t("survey.continue", "Continue")
            : t("survey.start", "Start")}
        </Button>
      </VStack>
    </Box>
  );
}
